(async () => {

    const params = new URLSearchParams(window.location.search);

    const freeGiftEnabled = window.adev.freeGiftEnabled;
    const freeGiftVariantId = window.adev.freeGiftVariantId;
    const freeGiftUrlParameter = window.adev.freeGiftUrlParameter;

    if (!freeGiftEnabled || !freeGiftVariantId || !freeGiftUrlParameter) return;


    /**
     * Get the current Shopify cart.
     */
    async function getCart() {
        const response = await fetch(
            window.Shopify.routes.root + 'cart.js'
        );

        if (!response.ok) {
            throw new Error('Could not load cart');
        }

        return response.json();
    }


    /**
     * Mark the cart as part of the free gift promotion.
     */
    async function activateFreeGiftOffer() {
        const response = await fetch(
            window.Shopify.routes.root + 'cart/update.js', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    attributes: {
                        free_gift_offer_active: 'true'
                    }
                })
            }
        );

        if (!response.ok) {
            throw new Error('Could not update free gift cart attribute');
        }
    }


    /**
     * Add the free gift if it isn't already in the cart.
     */
    async function addFreeGift() {
        const cart = await getCart();

        const giftAlreadyInCart = cart.items.some(
            item =>
            Number(item.variant_id) ===
            Number(freeGiftVariantId)
        );

        if (giftAlreadyInCart) {
            return false;
        }

        const response = await fetch(
            window.Shopify.routes.root + 'cart/add.js', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify({
                    items: [{
                        id: Number(freeGiftVariantId),
                        quantity: 1
                    }]
                })
            }
        );

        const responseData = await response.json();

        if (!response.ok) {
            const description =
                responseData?.description ||
                responseData?.message ||
                '';

            /**
             * Shopify can occasionally report that the item is already
             * at its maximum quantity even when the previous cart.js
             * response did not yet contain the latest cart state.
             *
             * In that case the gift is already in the cart,
             * so this is a successful state for our promotion flow.
             */
            if (
                response.status === 422 &&
                description
                .toLowerCase()
                .includes('maximum quantity')
            ) {
                return false;
            }

            console.error('Free gift add failed:', {
                status: response.status,
                variantId: freeGiftVariantId,
                response: responseData
            });

            throw new Error(
                description || 'Could not add free gift'
            );
        }

        return true;
    }


    /**
     * Refresh all cart-items-component instances
     * using Shopify's Section Rendering API.
     *
     * This updates:
     * - cart products
     * - prices
     * - discounts
     * - free gift messaging
     * - progress bar
     *
     * without reloading the page.
     */
    async function refreshCartComponents() {
        const cartComponents = Array.from(
            document.querySelectorAll('cart-items-component[data-section-id]')
        );

        if (!cartComponents.length) return;

        const sectionIds = [
            ...new Set(
                cartComponents
                .map(component => component.dataset.sectionId)
                .filter(Boolean)
            )
        ];

        if (!sectionIds.length) return;

        const url = new URL(window.location.href);

        url.search = '';
        url.searchParams.set(
            'sections',
            sectionIds.join(',')
        );

        const response = await fetch(url.toString(), {
            headers: {
                'X-Requested-With': 'XMLHttpRequest'
            }
        });

        if (!response.ok) {
            throw new Error('Could not refresh cart sections');
        }

        const sections = await response.json();

        sectionIds.forEach(sectionId => {
            const sectionHTML = sections[sectionId];

            if (!sectionHTML) return;

            const parsedDocument = new DOMParser().parseFromString(
                sectionHTML,
                'text/html'
            );

            const newCartComponent = parsedDocument.querySelector(
                `cart-items-component[data-section-id="${sectionId}"]`
            );

            const currentCartComponent = document.querySelector(
                `cart-items-component[data-section-id="${sectionId}"]`
            );

            if (newCartComponent && currentCartComponent) {
                currentCartComponent.replaceWith(newCartComponent);
            }
        });
    }


    /**
     * Main free gift action.
     *
     * Used by:
     * 1. Welcome email URL
     * 2. "Add free keyring" button in cart drawer
     */
    async function handleFreeGift() {
        await addFreeGift();
        await activateFreeGiftOffer();
    }


    /**
     * ----------------------------------------
     * WELCOME EMAIL URL FLOW
     * ----------------------------------------
     *
     * Example:
     * ?free-gift=1
     *
     * The parameter name itself comes
     * from Theme Settings.
     */
    if (params.get(freeGiftUrlParameter) === '1') {
        try {
            await handleFreeGift();

            /*
             * Remember that the drawer needs to open
             * after the clean page load.
             */
            sessionStorage.setItem(
                'open-free-gift-cart',
                '1'
            );

            /*
             * Remove the promotion parameter.
             *
             * This prevents refreshing the page from
             * triggering the free gift action again.
             */
            window.location.replace(
                window.location.pathname
            );

        } catch (error) {
            console.error(
                'Free gift promotion error:',
                error
            );
        }

        return;
    }


    /**
     * ----------------------------------------
     * RE-ADD GIFT FROM CART DRAWER
     * ----------------------------------------
     *
     * This DOES NOT reload or navigate the page.
     *
     * Uses event delegation because the cart drawer
     * itself can be re-rendered dynamically.
     */
    document.addEventListener('click', async (event) => {
        const readdButton = event.target.closest(
            '[data-free-gift-readd]'
        );

        if (!readdButton) return;

        event.preventDefault();

        /*
         * Prevent double clicks while processing.
         */
        if (readdButton.dataset.loading === 'true') {
            return;
        }

        readdButton.dataset.loading = 'true';
        readdButton.setAttribute(
            'aria-disabled',
            'true'
        );

        try {
            await handleFreeGift();

            /*
             * Re-render the cart from Shopify so
             * discount, prices and messages are fresh.
             */
            await refreshCartComponents();

        } catch (error) {
            console.error(
                'Could not re-add free gift:',
                error
            );

            /*
             * Only restore the button if it still exists.
             * A successful refresh replaces this element.
             */
            readdButton.dataset.loading = 'false';
            readdButton.removeAttribute(
                'aria-disabled'
            );
        }
    });


    /**
     * ----------------------------------------
     * OPEN DRAWER AFTER WELCOME LINK
     * ----------------------------------------
     */
    if ( sessionStorage.getItem('open-free-gift-cart' ) === '1' ) {
        sessionStorage.removeItem(
            'open-free-gift-cart'
        );

        const cartTrigger = document.querySelector(
            '[data-testid="cart-drawer-trigger"]'
        );

        if (cartTrigger) {
            cartTrigger.click();
        }
    }

})();